import { useApi } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarCard, DonutCard } from "@/components/common/Charts";
import { ChartInsight } from "@/components/common/ChartInsight";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtCurrency, fmtNum, VENDOR_LABEL } from "@/lib/format";
import { toast } from "sonner";
import { Download, Star } from "lucide-react";

export default function UsageValue() {
  const { data, isLoading, error } = useApi("usage", "/usage/analytics");
  if (error) return <ErrorState />;

  return (
    <div>
      <PageHeader eyebrow="Navitra Lens" title="Usage & Value" subtitle="Adoption, use-case mix and fair cross-vendor comparison on the same use cases.">
        <Button size="sm" variant="outline" onClick={() => toast.success("Leadership pack exported")} data-testid="export-pack"><Download className="h-4 w-4 mr-1.5" />Export leadership pack</Button>
      </PageHeader>

      {isLoading || !data ? <LoadingBlock /> : (
        <div className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Adoption funnel</CardTitle></CardHeader>
              <CardContent>
                <ChartInsight title="Adoption funnel" app="Usage & Value"
                  context="How users progress from licensed to power users of governed AI."
                  data={data.adoption_funnel.map((s) => ({ label: s.stage, value: s.value }))}>
                  <div className="space-y-3">
                    {data.adoption_funnel.map((s, i) => {
                      const max = data.adoption_funnel[0].value;
                      return (
                        <div key={s.stage}>
                          <div className="flex justify-between text-sm mb-1"><span>{s.stage}</span><span className="mono">{fmtNum(s.value)}</span></div>
                          <div className="h-7 rounded-md bg-muted overflow-hidden"><div className="h-full rounded-md bg-navy/80 flex items-center justify-end pr-2" style={{ width: `${(s.value / max) * 100}%` }}><span className="text-[10px] text-nav-text">{((s.value / max) * 100).toFixed(0)}%</span></div></div>
                        </div>
                      );
                    })}
                  </div>
                </ChartInsight>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Use-case mix</CardTitle></CardHeader>
              <CardContent>
                <ChartInsight title="Use-case mix" app="Usage & Value"
                  context={`Distribution of AI usage across the top use cases; ${data.use_case_mix[0]?.use_case || "the leading use case"} dominates.`}
                  data={data.use_case_mix.slice(0, 6).map((u) => ({ label: u.use_case, value: u.count }))}>
                  <DonutCard data={data.use_case_mix.slice(0, 6)} nameKey="use_case" valueKey="count" />
                </ChartInsight>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Cross-vendor comparison</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead className="text-right">Requests</TableHead><TableHead className="text-right">Cost / task</TableHead><TableHead className="text-right">Avg latency</TableHead><TableHead className="text-right">Satisfaction</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.vendor_comparison.map((v) => (
                    <TableRow key={v.vendor}>
                      <TableCell className="font-medium">{VENDOR_LABEL[v.vendor]}</TableCell>
                      <TableCell className="text-right mono">{fmtNum(v.requests)}</TableCell>
                      <TableCell className="text-right mono">{fmtCurrency(v.cost_per_task)}</TableCell>
                      <TableCell className="text-right mono">{fmtNum(v.avg_latency_ms)} ms</TableCell>
                      <TableCell className="text-right"><span className="inline-flex items-center gap-1 mono"><Star className="h-3.5 w-3.5 text-gold fill-gold" />{v.satisfaction}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
